// src/pages/user/UserProfile.jsx
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEdit, FaEnvelope, FaUserPlus, FaUserCheck } from 'react-icons/fa';

import { UserContext } from '../../context/userContext';
import Loader from '../../components/common/Loader';
import PostItem from '../../components/post/PostItem';

const UserProfile = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  
  const isOwnProfile = currentUser && id === currentUser.id;

  // Fetch user and posts data
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        // Fetch user profile
        const headers = {};
        if (currentUser?.token) {
          headers.Authorization = `Bearer ${currentUser.token}`;
        }

        const userResponse = await axios.get(`${process.env.REACT_APP_BASE_URL}/users/${id}`, {
          headers
        });
        
        setUser(userResponse.data);
        
        // Check if current user is following this user
        if (currentUser && id !== currentUser.id) {
          try {
            const followResponse = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/followers/check/${id}`,
              { headers: { Authorization: `Bearer ${currentUser.token}` } }
            );
            setIsFollowing(followResponse.data.following);
          } catch (error) {
            console.error("Error checking follow status:", error);
          }
        }
        
        // Fetch user posts
        const postsResponse = await axios.get(`${process.env.REACT_APP_BASE_URL}/posts/users/${id}`, {
          headers
        });
        
        setPosts(postsResponse.data.posts || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user profile');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [id, currentUser]);

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        // Unfollow
        await axios.delete(`${process.env.REACT_APP_BASE_URL}/followers/${id}`, {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        });
      } else {
        // Follow
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/followers`,
          { userId: id },
          { headers: { Authorization: `Bearer ${currentUser.token}` } }
        );
      }
      
      // Toggle following state
      setIsFollowing(!isFollowing);
      
      // Update follower count in user object
      setUser(prev => ({
        ...prev,
        followers: isFollowing ? prev.followers - 1 : prev.followers + 1
      }));
    } catch (error) {
      console.error("Error toggling follow:", error);
    } finally {
      setFollowLoading(false);
    }
  };

  // Navigate to messages with this user
  const handleMessage = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    navigate(`/messages/${id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error}</p>
          <Link to="/" className="mt-4 inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-gray-50 border border-gray-200 text-gray-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">User Not Found</h2>
          <p>The user you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="mt-4 inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <section className="py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* User Avatar */}
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 mx-auto md:mx-0 border-4 border-gray-50">
                <img 
                  src={`${process.env.REACT_APP_ASSETS_URL}/uploads/profile/${user.avatar || 'default-avatar.png'}`} 
                  alt={user.name}
                  className="w-full h-full object-cover" 
                />
              </div>
              
              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{user.name}</h1>
                
                {user.bio && (
                  <p className="text-gray-700 mb-4">{user.bio}</p>
                )}
                
                {/* User Stats */}
                <div className="flex justify-center md:justify-start gap-6 mb-4">
                  <div className="text-center">
                    <span className="block text-xl font-bold text-gray-900">{user.posts || 0}</span>
                    <span className="text-sm text-gray-500">Posts</span>
                  </div>
                  
                  <Link to={`/users/${id}/followers`} className="text-center">
                    <span className="block text-xl font-bold text-gray-900">{user.followers || 0}</span>
                    <span className="text-sm text-gray-500">Followers</span>
                  </Link>
                  
                  <Link to={`/users/${id}/following`} className="text-center">
                    <span className="block text-xl font-bold text-gray-900">{user.following || 0}</span>
                    <span className="text-sm text-gray-500">Following</span>
                  </Link>
                </div>
                
                {/* User Actions */}
                <div className="flex gap-3 justify-center md:justify-start">
                  {isOwnProfile ? (
                    <Link 
                      to="/dashboard" 
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
                    >
                      <FaEdit className="mr-2" /> Dashboard
                    </Link>
                  ) : (
                    <>
                      <button 
                        className={`inline-flex items-center px-4 py-2 rounded-md transition-colors ${
                          isFollowing 
                            ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' 
                            : 'bg-primary text-white hover:bg-opacity-90'
                        }`}
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                      >
                        {followLoading ? (
                          <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : isFollowing ? (
                          <FaUserCheck className="mr-2" />
                        ) : (
                          <FaUserPlus className="mr-2" />
                        )}
                        {isFollowing ? 'Following' : 'Follow'}
                      </button>
                      
                      <button 
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                        onClick={handleMessage}
                      >
                        <FaEnvelope className="mr-2" /> Message
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Posts Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Posts</h2>
          
          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <PostItem
                  key={post._id}
                  postID={post._id}
                  thumbnail={post.thumbnail}
                  category={post.category}
                  title={post.title}
                  description={post.description}
                  authorID={post.creator}
                  createdAt={post.createdAt}
                  likes={post.likes}
                  comments={post.comments}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <h3 className="text-xl font-medium text-gray-800 mb-4">No posts yet</h3>
              {isOwnProfile && (
                <Link 
                  to="/create" 
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
                >
                  Create Your First Post
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default UserProfile;